/**
 * asteroidSystem.ts
 * Pool, spawner, update, and projectile-vs-asteroid collision for World 2.
 *
 * Three responsibilities, kept in one module because they share the
 * `asteroidPool` singleton:
 *   1. `resetAsteroids()` / `updateAsteroidSpawner()` — Poisson-ish spawner
 *      that introduces new asteroids at the right edge of the camera window.
 *   2. `updateAsteroids()` — integrates motion, reaps dead instances.
 *   3. `resolveShipProjectileAsteroidCollisions()` — pairwise AABB tests
 *      between live ship projectiles and live asteroids; destructible
 *      asteroids take 1 HP per hit, indestructible ones eat the projectile
 *      but stay alive.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module asteroidSystem
 */

import { spaceState } from './spaceState';
import { AsteroidEntity } from '../entities/AsteroidEntity';
import { ObjectPool } from '../utils/objectPool';
import { spawnExplosion } from './explosionVfx';
import type { LevelRuntime } from './gameState';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import {
  ASTEROID_MAX_ACTIVE,
  ASTEROID_SPAWN_INTERVAL_S,
  ASTEROID_SIZE_PX,
  ASTEROID_SPAWN_X_OFFSET,
  ASTEROID_SPAWN_Y_MIN,
  ASTEROID_SPAWN_Y_MAX_OFFSET,
  type AsteroidSize,
} from '../../constants/asteroidConstants';

/** Reusable empty level for asteroid updates — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/** Module-singleton asteroid pool — pre-warmed at module load. */
const asteroidPool = new ObjectPool<AsteroidEntity>(
  () => new AsteroidEntity(),
  ASTEROID_MAX_ACTIVE,
);
asteroidPool.prewarm(ASTEROID_MAX_ACTIVE);

/**
 * Injectable RNG so tests can pin spawn behaviour. Defaults to `Math.random`.
 * Production code MUST NOT call `setAsteroidRng` outside of tests.
 *
 * @internal
 */
let rng: () => number = Math.random;

/**
 * Replace the spawner's RNG. Test-only seam — production code uses the
 * default `Math.random`.
 *
 * @param fn - Zero-arg function returning a number in `[0, 1)`.
 * @internal
 */
export function setAsteroidRng(fn: () => number): void {
  rng = fn;
}

/**
 * Restore the spawner's RNG to `Math.random`. Call from test teardown so
 * pinned sequences do not leak across suites.
 *
 * @internal
 */
export function resetAsteroidRng(): void {
  rng = Math.random;
}

/**
 * Release every active asteroid back to the pool. Called from
 * `SpaceScene.init()` and `SpaceScene.destroy()` so asteroids never
 * bleed across sessions.
 *
 * Owns the `spaceState.asteroids` active-list lifecycle only. The module
 * also holds mutable RNG state (`rng`), which is owned separately by
 * `setAsteroidRng` / `resetAsteroidRng` — tests that pin the RNG must
 * call `resetAsteroidRng()` in teardown; `resetAsteroids()` deliberately
 * does NOT touch it so production reset paths cannot stomp on a still-
 * useful RNG override.
 */
export function resetAsteroids(): void {
  for (const a of spaceState.asteroids) {
    asteroidPool.release(a);
  }
  spaceState.asteroids = [];
}

/**
 * Roll one spawn tick. Uses a Poisson-like process: each fixed tick has
 * probability `dt / ASTEROID_SPAWN_INTERVAL_S` of producing one asteroid.
 *
 * DECISION: Probability gate rather than a strict accumulator so spawns
 * naturally jitter rather than landing on a perfectly regular cadence.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateAsteroidSpawner(dt: number): void {
  if (spaceState.asteroids.length >= ASTEROID_MAX_ACTIVE) return;

  const p = dt / ASTEROID_SPAWN_INTERVAL_S;
  if (rng() >= p) return;

  const size = pickSize();
  const dim = ASTEROID_SIZE_PX[size];
  const spawnX = spaceState.cameraX + VIEWPORT_WIDTH + ASTEROID_SPAWN_X_OFFSET;
  const yMax = VIEWPORT_HEIGHT - dim - ASTEROID_SPAWN_Y_MAX_OFFSET;
  const yRange = Math.max(0, yMax - ASTEROID_SPAWN_Y_MIN);
  const spawnY = ASTEROID_SPAWN_Y_MIN + Math.floor(rng() * (yRange + 1));

  const asteroid = asteroidPool.acquire();
  if (asteroid === null) return; // Pool exhausted — drop the spawn silently.
  asteroid.spawn(spawnX, spawnY, size);
  spaceState.asteroids.push(asteroid);
}

/**
 * Update all live asteroids and reap dead ones back into the pool.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateAsteroids(dt: number): void {
  for (let i = spaceState.asteroids.length - 1; i >= 0; i--) {
    const a = spaceState.asteroids[i];
    a.update(dt, EMPTY_SPACE_LEVEL);
    if (a.dead) {
      asteroidPool.release(a);
      spaceState.asteroids.splice(i, 1);
    }
  }
}

/**
 * Pairwise AABB collision between live ship projectiles and live asteroids.
 *
 * Rules:
 *   - Destructible asteroid + projectile  → asteroid takes 1 HP, projectile dies.
 *   - Indestructible asteroid + projectile → projectile dies, asteroid lives.
 *
 * Asteroids whose LEFT edge is past the right edge of the viewport are
 * skipped: those are entirely off-screen, and the projectile cull margin
 * (`SHIP_PROJECTILE_OFFSCREEN_MARGIN`) keeps a few px of projectile life
 * past the viewport. Without this gate, a wide xl asteroid drifting into
 * that margin could absorb a shot the player never sees connect.
 * `ASTEROID_SPAWN_X_OFFSET` only protects the first frame; this gate
 * protects every subsequent frame until the asteroid enters the viewport.
 *
 * Projectile cleanup (splice from the array, release to pool) is left to
 * `updateShipProjectiles()` so this function has a single responsibility:
 * decide outcomes. We just flag the projectile `dead = true` here.
 *
 * Complexity: O(P*A). With caps of 20 projectiles × 24 asteroids = 480 AABB
 * tests max per tick, well under the engine's 1 ms budget.
 */
export function resolveShipProjectileAsteroidCollisions(): void {
  const projectiles = spaceState.projectiles;
  const asteroids = spaceState.asteroids;
  const viewportRight = spaceState.cameraX + VIEWPORT_WIDTH;
  for (let i = 0; i < projectiles.length; i++) {
    const p = projectiles[i];
    if (p.dead) continue;
    const pRight = p.x + p.width;
    const pBottom = p.y + p.height;
    for (let j = 0; j < asteroids.length; j++) {
      const a = asteroids[j];
      if (a.dead) continue;
      // DECISION: Visibility gate. Skip asteroids whose LEFT edge is at or
      // past the viewport right — they are entirely off-screen and must not
      // absorb projectiles that the player still sees alive in the cull margin.
      if (a.x >= viewportRight) continue;
      // Inlined AABB overlap to avoid getHitbox allocation in the hot path.
      if (
        pRight <= a.x ||
        p.x >= a.x + a.width ||
        pBottom <= a.y ||
        p.y >= a.y + a.height
      ) {
        continue;
      }
      // Hit. Destructible asteroids take damage; indestructible ones just
      // absorb the shot. Either way the projectile dies.
      if (a.destructible) {
        // DECISION: `takeDamage` returns `true` on the kill frame.
        // Mirror the enemy-kill VFX path in `spaceEnemySystem.ts` so a
        // shattering asteroid produces the same six-frame burst at its
        // centre. Saturated VFX pool silently drops — cosmetics never
        // block gameplay.
        const killed = a.takeDamage(1);
        if (killed) {
          spawnExplosion(a.x + a.width / 2, a.y + a.height / 2);
        }
      }
      p.dead = true;
      p.active = false;
      break;
    }
  }
}

/**
 * Random size pick with a bias toward smaller asteroids (more frequent in
 * onboarding levels). Distribution: sm 40%, md 30%, lg 20%, xl 10%.
 */
function pickSize(): AsteroidSize {
  const r = rng();
  if (r < 0.4) return 'sm';
  if (r < 0.7) return 'md';
  if (r < 0.9) return 'lg';
  return 'xl';
}

/**
 * Test-only helper: number of asteroids currently parked in the pool
 * (i.e. inactive and ready to be re-acquired).
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _asteroidPoolAvailable(): number {
  return asteroidPool.available;
}
